import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    Link,
    Search,
    SubNavbar,
    Tab,
    Tabs,
    TabsList,
    useMediaQuery,
    type SearchProps,
} from '@bi-digital/react';
import type { FetcherError } from '@bino/utils/fetcher';
import { fetcher } from '@bino/utils/fetcher';
import useSWRImmutable from 'swr/immutable';
import { useSearchParams } from 'react-router';
import classNames from 'classnames';
import type { FrontpageResponse, SearchResponse, SearchTypes } from '../types';
import { toSearchType } from '../types';
import {
    AllIcon,
    CalendarIcon,
    ChevronRightIcon,
    GroupUnitIcon,
    JournalIcon,
    SchoolIcon,
    TogglebuttonIcon,
} from '@bi-digital/icons';
import { useTranslation } from 'react-i18next';
import { pageSize } from '../utils';
import { Result } from '../components/result/Result';

import styles from './search.module.css';
import { Filters } from '../components/filters/Filters';
import { Pagination } from '../components/pagination/Pagination';
import { NoResults } from '../components/no-results/NoResults';
import { useMemo, useState } from 'react';
import { Pending } from '@bino/utils/components/pending';
import { StartPage } from '../components/start-page/StartPage';

export type SearchPageProps = {
    findAnExpertUrl: string;
};

export const SearchPage = ({ findAnExpertUrl }: SearchPageProps) => {
    const { t, i18n } = useTranslation();
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const isMediumScreen = useMediaQuery('(width < 840px)');
    const isCompactScreen = useMediaQuery('(width < 600px)');

    const type: SearchTypes = toSearchType(searchParams.get('type'));

    const { data: frontPage } = useSWRImmutable<FrontpageResponse, FetcherError>(
        type === 'All' ? `/api/search/frontpage/?${searchParams.toString()}&lang=${i18n.language}` : null,
        fetcher.get<FrontpageResponse>,
        { keepPreviousData: true, errorRetryCount: 2 },
    );

    const { data, isLoading } = useSWRImmutable<SearchResponse, FetcherError>(
        type !== 'All' ? `/api/search/?${searchParams.toString()}&size=${pageSize}&lang=${i18n.language}` : null,
        fetcher.get<SearchResponse>,
        { keepPreviousData: true, errorRetryCount: 2 },
    );

    const handleSearch: SearchProps['onSubmit'] = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set('q', value);
            searchParams.set('page', '0');
            return searchParams;
        });
    };

    const handleTypeChange = (type: string) => {
        setSearchParams((s) => ({ type, q: s.get('q') || [], page: '0' }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetFilters = () => {
        setSearchParams((s) => ({ type, q: s.get('q') || [], page: '0' }));
        setFilterDialogOpen(false);
    };

    const filterCount = useMemo(
        () => Array.from(searchParams.entries()).filter(([key]) => !['q', 'type', 'page'].includes(key)).length,
        [searchParams],
    );

    return (
        <div className={styles.container} data-color-scheme='light' data-color='neutral'>
            <div className={styles.grid}>
                <div className={styles.header}>
                    <h1>{t('title')}</h1>
                    <div className={styles.searchRow}>
                        <Search
                            autoFocus={!isCompactScreen}
                            placeholder={t('placeholder')}
                            defaultValue={searchParams.get('q') || ''}
                            onSubmit={handleSearch}
                            onReset={handleSearch}
                        />
                        {isMediumScreen && type !== 'All' && (
                            <>
                                <Button
                                    onClick={() => setFilterDialogOpen(true)}
                                    variant='flat'
                                    endIcon={<TogglebuttonIcon aria-hidden='true' />}
                                >
                                    {t('filter.toggleButton')}
                                </Button>
                                <Dialog
                                    className={styles.filterDrawer}
                                    open={filterDialogOpen}
                                    onClose={() => setFilterDialogOpen(false)}
                                    anchor={isCompactScreen ? 'bottom' : 'right'}
                                >
                                    <DialogCloseButton
                                        aria-label={t('filter.closeButton')}
                                        onClick={() => setFilterDialogOpen(false)}
                                    />
                                    <DialogHeader>{t('filter.header')}</DialogHeader>
                                    <DialogContent>
                                        <Filters />
                                    </DialogContent>
                                    <DialogFooter>
                                        <Button
                                            className={styles.filterActionButton}
                                            variant='text'
                                            onClick={resetFilters}
                                        >
                                            {t('filter.resetButton')}
                                        </Button>
                                        <Button
                                            className={styles.filterActionButton}
                                            onClick={() => setFilterDialogOpen(false)}
                                        >
                                            {t('filter.applyButton')} ({filterCount})
                                        </Button>
                                    </DialogFooter>
                                </Dialog>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <SubNavbar className={styles.subNavbar}>
                <div className={classNames(styles.grid, styles.tabs)}>
                    <Tabs className={styles.header} value={type} onChange={handleTypeChange} data-color='primary'>
                        <TabsList>
                            <Tab value={'All' satisfies SearchTypes}>
                                <AllIcon aria-hidden='true' />
                                {t('tabs.all')}
                            </Tab>
                            <Tab value={'Programmes' satisfies SearchTypes}>
                                <SchoolIcon aria-hidden='true' />
                                {t('tabs.programmes')}
                            </Tab>
                            <Tab value={'Articles' satisfies SearchTypes}>
                                <JournalIcon aria-hidden='true' />
                                {t('tabs.articles')}
                            </Tab>
                            <Tab value={'Employees' satisfies SearchTypes}>
                                <GroupUnitIcon aria-hidden='true' />
                                {t('tabs.employees')}
                            </Tab>
                            <Tab value={'Events' satisfies SearchTypes}>
                                <CalendarIcon aria-hidden='true' />
                                {t('tabs.events')}
                            </Tab>
                        </TabsList>
                    </Tabs>
                </div>
            </SubNavbar>
            <div className={classNames(styles.grid, styles.content)}>
                {isMediumScreen && type === 'Employees' && (
                    <p className={styles.findExpertText}>
                        {t('findExpert.description')}{' '}
                        <Link href={findAnExpertUrl} color='neutral'>
                            {t('findExpert.linkText')}
                        </Link>
                    </p>
                )}
                {(data || frontPage) && (
                    <div className={classNames(styles.countContainer, styles.results)}>
                        {((type !== 'All' && data && data.results.length) || type === 'All') && (
                            <p className={styles.count}>
                                {t(`count.${type}`, {
                                    count: (type === 'All' ? frontPage?.facets.totalCount : data?.count) || 0,
                                })}
                            </p>
                        )}
                    </div>
                )}
                <div className={styles.results}>
                    {type === 'All' ? (
                        frontPage ? (
                            <StartPage results={frontPage} />
                        ) : (
                            <Pending />
                        )
                    ) : data ? (
                        data.results.length > 0 ? (
                            <div className={styles.list}>
                                {data.results.map((result) => (
                                    <Result key={result.id} result={result} />
                                ))}
                                <Pagination totalResults={data.count} />
                            </div>
                        ) : (
                            !isLoading && <NoResults type={type} />
                        )
                    ) : (
                        <Pending />
                    )}
                </div>
                {!isMediumScreen && type !== 'All' && data && (
                    <div className={styles.filters}>
                        {type === 'Employees' && (
                            <Card>
                                <CardContent className={styles.findExpertContent}>
                                    <h3>{t('findExpert.title')}</h3>
                                    <p>{t('findExpert.description')}</p>
                                    <Button variant='flat' asChild endIcon={<ChevronRightIcon aria-hidden='true' />}>
                                        <a href={findAnExpertUrl}>{t('findExpert.linkText')}</a>
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                        <Card>
                            <CardContent className={styles.filtersContent}>
                                <Filters />
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
};
